<template>
  <div>
    <HrPageLayout
      title="Employee Skills"
      subtitle="View and manage employee skills"
      icon="🛠️"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Training & Development' },
        { label: 'Employee Skills' }
      ]"
    >
      <template #header-actions>
        <button type="button" class="hr-btn hr-btn-primary" @click="openAdd">
          <i class="bx bx-plus"></i> Add Skill
        </button>
      </template>

      <div class="certifications-content">
        <div v-if="message" class="hr-alert" :class="messageSuccess ? 'hr-alert-success' : 'hr-alert-danger'">
          {{ message }}
          <button type="button" class="hr-btn hr-btn-sm" @click="message = ''">&times;</button>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <div class="hr-filter-group">
              <label class="hr-form-label">Employee (emp no)</label>
              <input v-model="filterEmpNo" class="hr-input" placeholder="Leave empty for all" style="width: 180px;" @keyup.enter="loadSkills">
            </div>
            <button type="button" class="hr-btn hr-btn-primary" @click="loadSkills">
              <i class="bx bx-search"></i> Load
            </button>
          </div>
          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted">Loading skills...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Skill</th>
                    <th>Category</th>
                    <th>Proficiency</th>
                    <th>Years exp</th>
                    <th>Acquired from</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(s, idx) in skills" :key="s.skillID">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ s.empNo }}</td>
                    <td>{{ s.skillName }}</td>
                    <td>{{ s.skillCategory || '—' }}</td>
                    <td><span class="hr-badge hr-badge-info">{{ s.proficiencyLevel }}</span></td>
                    <td>{{ s.yearsExperience != null ? s.yearsExperience : '—' }}</td>
                    <td>{{ s.acquiredFrom || '—' }}</td>
                    <td>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openEdit(s)"><i class="bx bx-edit"></i></button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="confirmDelete(s)"><i class="bx bx-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="skills.length === 0">
                    <td colspan="8" class="hr-text-center hr-py-4 hr-text-muted">No skills found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <div class="hr-modal-overlay" v-if="showModal" @click.self="showModal = false">
      <div class="hr-modal hr-modal-medium">
        <div class="hr-modal-header">
          <h3>{{ editingId ? 'Edit Skill' : 'Add Skill' }}</h3>
          <button type="button" class="hr-modal-close" @click="showModal = false">&times;</button>
        </div>
        <form @submit.prevent="saveSkill" class="hr-form-grid hr-p-3">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Employee No <span class="hr-text-danger">*</span></label>
            <input v-model="form.empNo" class="hr-input" required placeholder="empNo">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Skill name <span class="hr-text-danger">*</span></label>
            <input v-model="form.skillName" class="hr-input" required placeholder="Skill name">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Category</label>
            <input v-model="form.skillCategory" class="hr-input" placeholder="e.g. Technical">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Proficiency</label>
            <select v-model="form.proficiencyLevel" class="hr-select">
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Years experience</label>
            <input v-model.number="form.yearsExperience" class="hr-input" type="number" step="0.1" min="0" placeholder="0">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Acquired from</label>
            <select v-model="form.acquiredFrom" class="hr-select">
              <option value="experience">Experience</option>
              <option value="training">Training</option>
              <option value="education">Education</option>
              <option value="certification">Certification</option>
            </select>
          </div>
          <div class="hr-modal-footer span-full">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showModal = false">Cancel</button>
            <button type="submit" class="hr-btn hr-btn-primary" :disabled="saving">Save</button>
          </div>
        </form>
      </div>
    </div>

    <div class="hr-modal-overlay" v-if="deleteTarget" @click.self="deleteTarget = null">
      <div class="hr-modal hr-modal-small">
        <div class="hr-modal-header"><h3>Delete skill</h3><button type="button" class="hr-modal-close" @click="deleteTarget = null">&times;</button></div>
        <div class="hr-p-3">
          <p>Delete "{{ deleteTarget && deleteTarget.skillName }}"?</p>
          <div class="hr-mt-3 hr-flex gap-2">
            <button type="button" class="hr-btn hr-btn-secondary" @click="deleteTarget = null">Cancel</button>
            <button type="button" class="hr-btn hr-btn-danger" @click="doDelete" :disabled="deleting">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeSkills",
  components: { HrPageLayout },
  data() {
    return {
      skills: [],
      loading: false,
      message: '',
      messageSuccess: false,
      filterEmpNo: '',
      showModal: false,
      editingId: 0,
      saving: false,
      form: {
        empNo: '',
        skillName: '',
        skillCategory: '',
        proficiencyLevel: 'beginner',
        yearsExperience: null,
        acquiredFrom: 'experience'
      },
      deleteTarget: null,
      deleting: false
    };
  },
  mounted() {
    this.loadSkills();
  },
  methods: {
    async loadSkills() {
      this.loading = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "getEmployeeSkills");
      if (this.filterEmpNo.trim()) data.append("empNo", this.filterEmpNo.trim());
      else data.append("all", "1");
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) this.skills = res.data.data.skills || [];
      } catch (e) {
        this.message = e.message || 'Failed to load';
        this.messageSuccess = false;
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editingId = 0;
      this.form = { empNo: this.filterEmpNo || '', skillName: '', skillCategory: '', proficiencyLevel: 'beginner', yearsExperience: null, acquiredFrom: 'experience' };
      this.showModal = true;
    },
    openEdit(s) {
      this.editingId = s.skillID;
      this.form = {
        empNo: s.empNo,
        skillName: s.skillName || '',
        skillCategory: s.skillCategory || '',
        proficiencyLevel: s.proficiencyLevel || 'beginner',
        yearsExperience: s.yearsExperience ?? null,
        acquiredFrom: s.acquiredFrom || 'experience'
      };
      this.showModal = true;
    },
    async saveSkill() {
      this.saving = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "saveEmployeeSkill");
      data.append("skillID", this.editingId);
      data.append("empNo", this.form.empNo);
      data.append("skillName", this.form.skillName);
      data.append("skillCategory", this.form.skillCategory);
      data.append("proficiencyLevel", this.form.proficiencyLevel);
      if (this.form.yearsExperience != null && this.form.yearsExperience !== '') data.append("yearsExperience", this.form.yearsExperience);
      data.append("acquiredFrom", this.form.acquiredFrom);
      data.append("createdBy", sessionStorage.getItem("sessionNames") || '');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.message = res.data.message || 'Saved';
          this.messageSuccess = true;
          this.showModal = false;
          this.loadSkills();
        } else {
          this.message = res.data.message || 'Save failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Save failed';
        this.messageSuccess = false;
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(s) { this.deleteTarget = s; },
    async doDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      const data = new FormData();
      data.append("function", "deleteEmployeeSkill");
      data.append("skillID", this.deleteTarget.skillID);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.message = 'Deleted';
          this.messageSuccess = true;
          this.deleteTarget = null;
          this.loadSkills();
        } else {
          this.message = res.data.message || 'Delete failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Delete failed';
        this.messageSuccess = false;
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.certifications-content { padding: 0; }
.hr-alert { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; }
.hr-alert-success { background: #d1fae5; color: #065f46; }
.hr-alert-danger { background: #fee2e2; color: #991b1b; }
.hr-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.hr-modal { background: #fff; border-radius: 16px; max-width: 96vw; max-height: 90vh; overflow-y: auto; }
.hr-modal-medium { width: 560px; }
.hr-modal-small { width: 400px; }
.hr-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; }
.hr-modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #64748b; }
.hr-modal-footer { display: flex; gap: 12px; justify-content: flex-end; margin-top: 16px; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.hr-p-3 { padding: 20px; }
.hr-mt-3 { margin-top: 16px; }
.hr-flex.gap-2 { display: flex; gap: 8px; }
</style>
